import { assertCanModifyLabel, type UserRole } from "@/app/userRole";
import { apiClient } from "@/http";
import { createLogger } from "@/logging";
import { apiRoutes } from "./apiRoutes";
import { fetchAllPages, type PageQuery, type PageResp } from "./page";

const log = createLogger("api/label");

// 부여 method(provenance). backend entity/label/source.py 와 1:1 로 맞는다.
export type LabelSource = "manual" | "system" | "parquet" | "cv";

export type Label = {
  uuid: string;
  key: string;
  value: string;
  color: number;
  description: string | null;
  // 시드된 managed 카탈로그 소속 여부(read-only). 코드가 이 `(key, value)` 의 **존재**를
  // 보장한다는 뜻이다. 그래서 **삭제만** 막는다(서버 409). 색·설명은 운영자가 라벨 페이지에서
  // 고치는 표시 속성이다. 시드는 그 편집을 덮지 않는다. 프론트는 이 플래그로 삭제 UI 만 막는다.
  managed: boolean;
  // 이 라벨이 부여된 available dataset 수. archived 는 세지 않는다. 그래서 카탈로그·제출에서
  // 실제로 고를 수 있는 수와 같다. 이 값은 카탈로그 리소스 응답(/v1/labels) 전용 집계다.
  // dataset 상세 등에 포함되는 라벨 projection 에는 이 값이 없다. 그때 소비처는 배지를 생략한다.
  dataset_count?: number;
  created_at: string;
  modified_at: string;
};

export type LabelCreateReq = {
  key: string;
  value: string;
  color: number;
  description?: string | null;
};

export type LabelUpdateReq = {
  color?: number | null;
  description?: string | null;
};

export type LabelMutationOptions = {
  role: UserRole | null;
};

let labelCatalogCache: readonly Label[] | null = null;
let labelCatalogPromise: Promise<readonly Label[]> | null = null;
let labelCatalogEpoch = 0;

/**
 * 카탈로그 전체를 받아 클라이언트에서 거르는 방식이 맞는 규모의 상한.
 *
 * key·값 자동완성(새 라벨 만들기 창, 작업 축 토큰 입력)과 데이터셋 필터 바의 라벨 facet 은
 * 모두 이 전체 목록을 순회한다. `filterExistingLabels`·`deriveLabelSuggestions`·`newLabelBlocker`
 * 는 키 입력마다 전체를 순회한다. 그래서 목록이 커지면 네트워크보다 이 순회가 먼저 느려진다.
 *
 * 상한을 넘으면 축별로 서버에 필터를 맡길 시점이다(BE `docs/specs/label-catalog.md` 의 T1).
 * **자동 전환은 하지 않는다.** 평소 실행되지 않는 경로가 가장 나쁜 순간에 처음 실행되지 않게 한다.
 */
const LABEL_CATALOG_CLIENT_LIMIT = 500;

// 임계 경고는 세션당 한 번만 낸다. 무효화(뮤테이션·로그아웃)마다 다시 내면 소음이 된다.
let labelCatalogLimitWarned = false;

/**
 * 라벨 카탈로그 페이지.
 *
 * 서버는 `?search=`(key 또는 value 의 ILIKE contains)도 받는다. 그러나 여기 타입에는 두지 않는다.
 * 이 검색을 쓸 곳은 `LabelTableSource` 인데, 지금 서버 검색으로 옮기면 기능이 줄어든다:
 * - 클라이언트 필터는 `description` 과 facet 선택까지 AND 로 묶는다. 서버 `search` 는 key/value 만 본다.
 * - 전체 목록이 이미 세션 캐시에 있어 지금 검색은 네트워크 비용이 0이다.
 * 서버 검색으로 옮기는 시점은 위 임계를 넘을 때다.
 */
export async function listLabels(
  query: PageQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<Label>> {
  const response = await apiClient.get<PageResp<Label>>(apiRoutes.label.collection, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

export function listAllLabels(): Promise<readonly Label[]> {
  if (labelCatalogCache !== null) {
    return Promise.resolve(labelCatalogCache);
  }
  if (labelCatalogPromise !== null) {
    return labelCatalogPromise;
  }

  const requestEpoch = labelCatalogEpoch;
  const promise = fetchAllPages(listLabels)
    .then((labels) => {
      warnIfBeyondClientLimit(labels.length);
      if (requestEpoch === labelCatalogEpoch) {
        labelCatalogCache = labels;
      }
      return labels;
    })
    .finally(() => {
      if (labelCatalogPromise === promise) {
        labelCatalogPromise = null;
      }
    });

  labelCatalogPromise = promise;
  return promise;
}

function warnIfBeyondClientLimit(total: number): void {
  if (total <= LABEL_CATALOG_CLIENT_LIMIT || labelCatalogLimitWarned) {
    return;
  }
  labelCatalogLimitWarned = true;
  log.warn("Label catalog exceeded the client-side autocomplete limit.", {
    total,
    limit: LABEL_CATALOG_CLIENT_LIMIT,
    next: "Move key/value suggestions to server-side filtering (see BE docs/specs/label-catalog.md).",
  });
}

export function invalidateLabelCatalog(): void {
  labelCatalogEpoch += 1;
  labelCatalogCache = null;
  labelCatalogPromise = null;
}

export type CreateLabelResult = {
  label: Label;
  // 이번 요청이 실제로 만들었는가. false 면 같은 (key,value) 가 이미 있어서 그 라벨을 돌려받은 것이다.
  created: boolean;
};

/**
 * 라벨을 만들거나, 같은 (key,value) 가 이미 있으면 그것을 돌려받는다.
 *
 * 서버는 멱등 get-or-create 라 중복 생성이 409 가 아니다. 신규는 201, 기존은 200 으로만
 * 구분된다. 이 구분을 버리면 호출부는 남이 만든 라벨을 받은 경우를 알 수 없다. 그러면 실제로는
 * 만들지 않았는데 "만들었습니다" 라고 표시한다.
 */
export async function createLabel(
  payload: LabelCreateReq,
  options: LabelMutationOptions,
): Promise<CreateLabelResult> {
  assertCanModifyLabel(options.role);
  const response = await apiClient.post<Label>(apiRoutes.label.collection, payload);
  invalidateLabelCatalog();
  return { label: response.data, created: response.status === 201 };
}

export async function updateLabel(
  labelUuid: string,
  payload: LabelUpdateReq,
  options: LabelMutationOptions,
): Promise<Label> {
  assertCanModifyLabel(options.role);
  const response = await apiClient.patch<Label>(apiRoutes.label.detail(labelUuid), payload);
  invalidateLabelCatalog();
  return response.data;
}

export async function deleteLabel(labelUuid: string, options: LabelMutationOptions): Promise<void> {
  assertCanModifyLabel(options.role);
  await apiClient.delete(apiRoutes.label.detail(labelUuid));
  invalidateLabelCatalog();
}
